import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../ui/SafeIcon'

const { FiShield, FiStar, FiAward } = FiIcons

function ProductCard({ product, viewMode = 'grid' }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  // Helper to safely get localized string
  const getLocalized = (obj) => {
    if (!obj) return ''
    if (typeof obj === 'string') return obj
    return obj[lang] || obj['en'] || obj['de'] || ''
  }

  const name = getLocalized(product.name)
  const description = getLocalized(product.description)

  // Alibaba-style layout for Grid
  if (viewMode === 'grid') {
    return (
      <Link to={`/product/${product.id}`} className="group block h-full">
        <div className="bg-white rounded-lg border border-gray-200 hover:border-[#FF6600] hover:shadow-lg transition-all duration-200 h-full flex flex-col p-3">
          {/* Image */}
          <div className="relative aspect-square mb-3 overflow-hidden rounded-md bg-gray-100">
            <img src={product.image} alt={name} className="w-full h-full object-cover mix-blend-multiply" loading="lazy" />
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col">
            {/* Title */}
            <h3 className="text-sm text-gray-700 line-clamp-2 mb-2 group-hover:underline group-hover:text-[#FF6600] transition-colors leading-snug">
              {name}
            </h3>

            {/* Price */}
            <div className="mb-1">
              <span className="text-lg font-bold text-gray-900">{product.price.toFixed(2)} €</span>
              {product.originalPrice && (
                <span className="ml-2 text-xs text-gray-400 line-through">{product.originalPrice.toFixed(2)} €</span>
              )}
            </div>

            {/* MOQ */}
            <div className="text-xs text-gray-500 mb-3">
              {t('product.minOrder')}: {product.minOrderQuantity} {t('product.pieces')}
            </div>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* Supplier Info */}
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-700 truncate max-w-[70%]">{product.supplier.name}</span>
                <span className="text-[10px] text-gray-400">CN</span>
              </div>
              <div className="flex items-center space-x-2 text-[10px] text-gray-500">
                <span className="bg-gray-100 px-1 rounded font-medium">{product.supplier.years} YRS</span>
                <div className="flex items-center text-[#FF6600]">
                  <SafeIcon icon={FiShield} className="w-3 h-3 mr-0.5" />
                  <span>Verified</span>
                </div>
                {product.rating >= 4.5 && (
                  <SafeIcon icon={FiAward} className="w-3 h-3 text-yellow-500" />
                )}
              </div>
            </div>

            {/* Hover Action */}
            <div className="mt-3">
              <button className="w-full py-1.5 border border-[#FF6600] text-[#FF6600] text-xs font-bold rounded hover:bg-[#FF6600] hover:text-white transition-colors opacity-0 group-hover:opacity-100">
                {t('product.contactSupplier')}
              </button>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  // Alibaba-style layout for List
  return (
    <Link to={`/product/${product.id}`} className="group block">
      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-200 flex flex-col sm:flex-row gap-6">
        {/* Image */}
        <div className="w-full sm:w-48 aspect-square flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
          <img src={product.image} alt={name} className="w-full h-full object-cover" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-2 group-hover:underline group-hover:text-[#FF6600] line-clamp-2">
              {name}
            </h3>
            <div className="flex items-baseline space-x-3 mb-2">
              <span className="text-2xl font-bold text-gray-900">{product.price.toFixed(2)} €</span>
              <span className="text-sm text-gray-500">/ {t('product.pieces')}</span>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-gray-500 mb-4">
              <span>{t('product.minOrder')}: {product.minOrderQuantity} {t('product.pieces')}</span>
              <span className="flex items-center">
                <SafeIcon icon={FiShield} className="w-3 h-3 text-[#FF6600] mr-1" />
                {t('feature.tradeAssurance')}
              </span>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 mb-4">
              {description}
            </p>
          </div>

          {/* Supplier Small */}
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <span className="font-bold text-gray-700">{product.supplier.name}</span>
            <span>{product.supplier.years} YRS</span>
            <span className="text-gray-300">|</span>
            <span>{t('product.responseRate')}: 98%</span>
          </div>
        </div>

        {/* Right Actions */}
        <div className="w-full sm:w-48 flex-shrink-0 flex flex-col justify-center space-y-2 border-l border-gray-100 pl-6">
          <button className="w-full py-2 bg-[#FF6600] text-white text-sm font-bold rounded hover:bg-[#e55c00] transition-colors">
            {t('product.contactSupplier')}
          </button>
          <button className="w-full py-2 border border-gray-300 text-gray-700 text-sm font-bold rounded hover:bg-gray-50 transition-colors">
            {t('product.chatNow')}
          </button>
        </div>
      </div>
    </Link>
  )
}

export default ProductCard